import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api/client.js'
import { useNav, type View } from '../nav/NavContext.js'

interface Notice {
  id: string
  alert_type: string
  severity: 'Info' | 'Warning' | 'Critical'
  message: string | null
  source_table: string | null
  source_id: string | null
  triggered_at: string
  is_read: boolean
}
interface Pref {
  alert_type: string
  enabled: boolean
}

const TYPE_LABEL: Record<string, string> = {
  EwayExpiring: 'E-Way Bill expiring',
  EwayExpired: 'E-Way Bill expired',
  DocExpiring: 'Document expiry',
  CreditLimitBreach: 'Credit limit exceeded',
  TransitOverdue: 'Trip overdue',
  NotificationFailed: 'WhatsApp report failed',
}

/** Where clicking a notification takes you (Stage 12 §4.1 click-through). */
function target(n: Notice): { view: View; params?: Record<string, string> } {
  switch (n.source_table) {
    case 'eway_bills':
    case 'trips':
      return { view: 'trips' }
    case 'customers':
      return { view: 'masters', params: { tab: 'customers' } }
    case 'vehicle_documents':
      return { view: 'masters', params: { tab: 'vehicles' } }
    case 'driver_documents':
    case 'drivers':
      return { view: 'masters', params: { tab: 'drivers' } }
    case 'notification_settings':
      return { view: 'settings', params: { tab: 'integrations' } }
    default:
      return { view: 'dashboard' }
  }
}

const ago = (iso: string): string => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  if (m < 60) return `${m} min ago`
  if (m < 1440) return `${Math.round(m / 60)} h ago`
  return `${Math.round(m / 1440)} d ago`
}

/** Top-bar bell: unread badge, dropdown, mark-as-read, per-user type preferences. */
export function NotificationBell() {
  const { go } = useNav()
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<Notice[]>([])
  const [prefs, setPrefs] = useState<Pref[] | null>(null)
  const [showPrefs, setShowPrefs] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const box = useRef<HTMLDivElement>(null)

  const refreshCount = useCallback(() => {
    api.get<{ unread: number }>('/notifications/unread-count').then((r) => setUnread(r.unread)).catch(() => {})
  }, [])

  const loadList = useCallback(() => {
    api.get<Notice[]>('/notifications').then(setItems).catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not load notifications'))
  }, [])

  useEffect(() => {
    refreshCount()
    const t = setInterval(refreshCount, 60_000)
    return () => clearInterval(t)
  }, [refreshCount])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next) {
      setError(null)
      loadList()
      refreshCount()
    }
  }

  const click = async (n: Notice) => {
    if (!n.is_read) await api.post(`/notifications/${n.id}/read`).catch(() => {})
    setOpen(false)
    refreshCount()
    const t = target(n)
    go(t.view, t.params)
  }

  const readAll = async () => {
    await api.post('/notifications/read-all').catch(() => {})
    loadList()
    refreshCount()
  }

  const openPrefs = () => {
    setShowPrefs((v) => !v)
    if (!prefs) api.get<Pref[]>('/notifications/preferences').then(setPrefs).catch(() => setPrefs([]))
  }

  const setPref = async (p: Pref, enabled: boolean) => {
    setPrefs((cur) => cur?.map((x) => (x.alert_type === p.alert_type ? { ...x, enabled } : x)) ?? cur)
    await api.put(`/notifications/preferences/${p.alert_type}`, { enabled }).catch(() => {})
    loadList()
    refreshCount()
  }

  return (
    <div className="tf-bell" ref={box}>
      <button type="button" className="tf-bell-button" onClick={toggle} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && <span className="tf-bell-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="tf-bell-panel" role="dialog" aria-label="Notifications">
          <div className="tf-bell-head">
            <strong>Notifications</strong>
            <span>
              <button type="button" className="tf-button-link" onClick={openPrefs}>{showPrefs ? 'Back' : 'Choose what I see'}</button>
              {!showPrefs && unread > 0 && <button type="button" className="tf-button-link" onClick={() => void readAll()}>Mark all read</button>}
            </span>
          </div>

          {showPrefs ? (
            <div className="tf-bell-list">
              <p className="tf-note">These choices apply to you only.</p>
              {(prefs ?? []).map((p) => (
                <label key={p.alert_type} className="tf-bell-pref">
                  <input type="checkbox" checked={p.enabled} onChange={(e) => void setPref(p, e.target.checked)} />
                  {TYPE_LABEL[p.alert_type] ?? p.alert_type}
                </label>
              ))}
            </div>
          ) : (
            <div className="tf-bell-list">
              {error && <p className="tf-error">{error}</p>}
              {!error && items.length === 0 && <p className="tf-note">Nothing needs your attention.</p>}
              {items.map((n) => (
                <button key={n.id} type="button" className={`tf-bell-item${n.is_read ? '' : ' tf-bell-unread'}`} onClick={() => void click(n)}>
                  <span className={`tf-bell-dot tf-sev-${n.severity}`} aria-label={n.severity} />
                  <span>
                    <span className="tf-bell-type">{TYPE_LABEL[n.alert_type] ?? n.alert_type}</span>
                    <span className="tf-bell-msg">{n.message}</span>
                    <span className="tf-bell-time">{ago(n.triggered_at)}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
